import { ReactNode } from 'react';

import { ColumnDef, VisibilityState } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';

import { DataTable } from '@/components/ui/table/data-table';

import { Subscriber } from './columns';

interface AudienceTableProps {
  columns: ColumnDef<Subscriber>[];
  data: Subscriber[];
  rowCount: number;
  isLoading?: boolean;
  hasSearch?: boolean;
  isServerPaginated?: boolean;
  currentPageIndex?: number;
  setCurrentPageIndex?: (index: number) => void;
  fetchNextPage?: () => void;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  columnVisibility?: VisibilityState;
  onColumnVisibilityChange?: (visibility: VisibilityState) => void;
  paginationCenterContent?: ReactNode;
}

export const AudienceTable = ({
  columns,
  data,
  rowCount,
  isLoading,
  hasSearch,
  isServerPaginated,
  currentPageIndex,
  setCurrentPageIndex,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
  columnVisibility,
  onColumnVisibilityChange,
  paginationCenterContent,
}: AudienceTableProps) => {
  const { t } = useTranslation();

  const emptyStateMessage = hasSearch ? t('campaigns.audience_no_results_search') : undefined;

  // Draft paginates client-side; saved data uses server (cursor) pagination.
  const serverPaginationProps = isServerPaginated
    ? {
        pagination: true,
        currentPageIndex,
        setCurrentPageIndex,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
      }
    : {};

  return (
    <DataTable.Root<Subscriber, unknown>
      fill
      className="min-h-0 flex-1"
      columns={columns}
      data={data}
      rowCount={rowCount}
      resourceName="contact"
      isLoading={isLoading}
      emptyStateMessage={emptyStateMessage}
      columnVisibility={columnVisibility}
      onColumnVisibilityChange={onColumnVisibilityChange}
      autoResetPageIndex={!isServerPaginated}
      {...serverPaginationProps}
    >
      <DataTable.Content />
      <DataTable.Pagination centerContent={paginationCenterContent} />
    </DataTable.Root>
  );
};
